import { RouteTransition } from "@/features/auth/route-transition/RouteTransition";
import { configManager } from "@/features/client-config/services/config-manager";
import { useInitializeWebSocket } from "@/features/radar/hooks/use-initialize-web-socket";
import { useGlobalShotDetection } from "@/features/shot-tracking/hooks/use-global-shot-detection";
import { useAuth } from "@/hooks/use-auth";
import { lazyNamed } from "@/router/lazyNamed";
import React, { Suspense, useEffect } from "react";
import {
  Route,
  Navigate,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";

const AuthCallback = lazyNamed(
  () => import("@/pages/auth-callback/AuthCallback"),
  "AuthCallback"
);
const HomeScreen = lazyNamed(
  () => import("@/pages/home/HomeScreen"),
  "HomeScreen"
);
const SessionScreen = lazyNamed(
  () => import("@/pages/session/SessionScreen"),
  "SessionScreen"
);
const SetupScreen = lazyNamed(
  () => import("@/pages/setup/SetupScreen"),
  "SetupScreen"
);
const VgCalibratorScreen = lazyNamed(
  () => import("@/pages/vg-calibrator/VgCalibratorScreen"),
  "VgCalibratorScreen"
);

export const AnimatedRoutes: React.FC = () => {
  "use no memo";
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  // Initialize WebSocket and fetch radar configs
  useInitializeWebSocket();

  // Always listen for shots globally and handle navigation
  useGlobalShotDetection();

  // Check for client configuration on home route and redirect to setup if not registered
  useEffect(() => {
    if (location.pathname !== "/" && location.pathname !== "/welcome") {
      return;
    }

    const checkAppState = async () => {
      const isRegistered = await configManager.isRegistered();
      if (!isRegistered) {
        navigate("/setup", { replace: true });
      }
    };

    checkAppState();
  }, [location.pathname, navigate, isAuthenticated, isLoading]);

  // Prefetch the next likely route chunks on idle.
  // Chunks are precached by the SW so this mainly benefits first visit (before SW installs)
  // and warms the browser module cache for instant transitions.
  useEffect(() => {
    const prefetch = () => {
      switch (location.pathname) {
        case "/":
          void import("@/pages/setup/SetupScreen");
          void import("@/pages/session/SessionScreen");
          break;
        case "/login":
        case "/setup":
          void import("@/pages/session/SessionScreen");
          break;
      }
    };

    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(prefetch);
      return () => cancelIdleCallback(id);
    } else {
      const timer = setTimeout(prefetch, 2000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  return (
    <Suspense fallback={null}>
      <Routes location={location}>
        {/* Authentication Callback - Public route */}
        <Route
          path="/account/callback"
          element={
            <RouteTransition>
              <AuthCallback />
            </RouteTransition>
          }
        />

        {/* Legacy route - manual login merged into /setup */}
        <Route path="/login" element={<Navigate to="/setup" replace />} />

        {/* Setup Screen - First time configuration - Protected */}
        <Route
          path="/setup"
          element={
            <RouteTransition>
              <SetupScreen />
            </RouteTransition>
          }
        />

        {/* Legacy route - merged into /setup */}
        <Route
          path="/device-setup"
          element={<Navigate to="/setup" replace />}
        />

        {/* Welcome Screen - Public (works offline) */}
        <Route
          path="/"
          element={
            <RouteTransition>
              <HomeScreen />
            </RouteTransition>
          }
        />

        {/* Session Screen - Public (works offline) */}
        <Route
          path="/session"
          element={
            <RouteTransition>
              <SessionScreen />
            </RouteTransition>
          }
        />

        {/* VG Image Map Calibrator - Debug tooling (lazy, no prefetch) */}
        <Route
          path="/vg-calibrator"
          element={
            <RouteTransition>
              <VgCalibratorScreen />
            </RouteTransition>
          }
        />

        {/* Fallback to welcome screen */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};
